import { type ConditionDto, type ObservationDto, useConditions, usePatients } from "@repo/data-store";
import { SNOMED_SYSTEM as SNOMED } from "@repo/fhir-systems";
import { Button } from "@repo/ui/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/components/ui/dialog";
import { Label } from "@repo/ui/components/ui/label";
import { Textarea } from "@repo/ui/components/ui/textarea";
import { OptionGroupField } from "@repo/ui/form-fields";
import { useEffect, useMemo } from "react";
import {
  DEFAULT_SYMPTOM_FORM_VALUES,
  recordSymptomFromForm,
  type SymptomFormValues,
  symptomFormSchema,
  updateSymptomFromForm,
  useAppForm,
} from "@/features/patients/forms/use-symptom-form";
import { conditionToggleOption } from "@/shared/common/conditions-addressed-list";
import { type CodeValue, fromCodeableConcept, toCodeableConcept } from "@/shared/forms/code-value";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import { useSubmitOnce } from "@/shared/forms/submit-once";
import { HpoCodePicker } from "@/shared/pickers/hpo-code-picker";

export interface AddSymptomDialogProps {
  /** Patient context. Required to enumerate attachable Conditions. */
  patientId: string | null;
  /** Optional Condition to pre-select in the attach list (e.g. opened from a row). */
  preselectedConditionId: string | null;
  /** Present when correcting an existing symptom; null for a new record. */
  editing?: ObservationDto | null;
  open: boolean;
  onClose: () => void;
  onExited: () => void;
}

const INVALID_VERIFICATION: ReadonlySet<string> = new Set(["refuted", "entered-in-error"]);
const INACTIVE_CLINICAL: ReadonlySet<string> = new Set(["resolved", "inactive"]);

function sourceFromCategory(obs: ObservationDto): "patient-reported" | "clinician-observed" {
  const code = obs.category?.map((c) => c.code).find((c) => c === "survey" || c === "exam");
  return code === "survey" ? "patient-reported" : "clinician-observed";
}

function codeLabel(c: { display?: string; text?: string; code?: string }): string {
  return c.display ?? c.text ?? c.code ?? "Symptom";
}

/** SNOMED severity qualifiers offered for a symptom. `null` = unspecified. */
const SOURCE_OPTIONS = [
  { value: "clinician-observed", label: "Clinician-observed" },
  { value: "patient-reported", label: "Patient-reported" },
] as const;

const SEVERITY_OPTIONS: ReadonlyArray<{ key: string; label: string; concept: CodeValue | null }> = [
  { key: "none", label: "None", concept: null },
  { key: "255604002", label: "Mild", concept: { system: SNOMED, code: "255604002", display: "Mild" } },
  { key: "6736007", label: "Moderate", concept: { system: SNOMED, code: "6736007", display: "Moderate" } },
  { key: "24484000", label: "Severe", concept: { system: SNOMED, code: "24484000", display: "Severe" } },
];

/** Dialog for recording or correcting a symptom Observation. */
export function AddSymptomDialog({
  patientId,
  preselectedConditionId,
  editing,
  open,
  onClose,
  onExited,
}: AddSymptomDialogProps) {
  const { patients, isLoading: isLoadingPatients } = usePatients();

  const isEditing = editing != null;
  const patientLocked = patientId != null || isEditing;
  const { conditions } = useConditions(patientId ? { patientId } : undefined);

  const attachableConditions = useMemo<ConditionDto[]>(
    () =>
      conditions.filter(
        (c) => !INVALID_VERIFICATION.has(c.verificationStatus ?? "") && !INACTIVE_CLINICAL.has(c.clinicalStatus ?? ""),
      ),
    [conditions],
  );

  const initialAttached = preselectedConditionId ? [preselectedConditionId] : [];

  // Flat read-model codes travel straight into form state; see `code-value.ts`
  // for why a `CodeableConcept` must never live in a TanStack Form value type.
  const defaultValues: SymptomFormValues = editing
    ? {
        patientId: editing.patientId,
        code: editing.code,
        source: sourceFromCategory(editing),
        severity: editing.valueCodeableConcept ?? null,
        note: editing.notes?.[0]?.text ?? "",
        attachedConditionIds: [],
      }
    : {
        ...DEFAULT_SYMPTOM_FORM_VALUES,
        patientId: patientId ?? "",
        attachedConditionIds: initialAttached,
      };

  const { once } = useSubmitOnce();
  const form = useAppForm({
    defaultValues,
    validators: { onDynamic: symptomFormSchema },
    onSubmit: ({ value }) =>
      once((entityId) => {
        if (editing) updateSymptomFromForm(value, editing.observationId);
        else recordSymptomFromForm(value, entityId);
        onClose();
      }),
    onSubmitInvalid: focusFirstInvalid,
  });

  // If the user opened from a global context (no patient) and then picks one,
  // we don't auto-attach anything — they explicitly choose.
  useEffect(() => {
    void patients;
  }, [patients]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      onOpenChangeComplete={(next) => !next && onExited()}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogTitle>{isEditing ? "Edit Symptom" : "Record Symptom"}</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-4"
        >
          <form.AppField name="patientId">
            {(field) => (
              <field.PatientField
                label="Patient"
                patients={patients}
                isLoading={isLoadingPatients}
                disabled={patientLocked}
              />
            )}
          </form.AppField>

          {isEditing && editing ? (
            <div className="flex flex-col gap-1.5">
              <Label>Symptom</Label>
              <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                <span className="text-sm font-medium">{codeLabel(editing.code)}</span>
                {editing.code.code && (
                  <code className="inline-flex h-5 items-center rounded border px-1.5 font-mono text-xs text-muted-foreground">
                    {editing.code.code}
                  </code>
                )}
              </div>
            </div>
          ) : (
            <form.AppField name="code">
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <Label>Symptom</Label>
                  {/* Picker speaks FHIR; form state holds the flat shape. */}
                  <HpoCodePicker
                    value={field.state.value ? toCodeableConcept(field.state.value) : undefined}
                    onChange={(v) => field.handleChange(fromCodeableConcept(v))}
                  />
                </div>
              )}
            </form.AppField>
          )}

          <form.AppField name="source">
            {(field) => <field.OptionGroupField size="sm" label="Source" options={[...SOURCE_OPTIONS]} />}
          </form.AppField>

          <form.AppField name="severity">
            {(field) => {
              // The field holds a SNOMED concept (or null); the picker speaks keys.
              return (
                <OptionGroupField
                  size="sm"
                  label="Severity (optional)"
                  value={field.state.value?.code ?? "none"}
                  onChange={(key) => field.handleChange(SEVERITY_OPTIONS.find((o) => o.key === key)?.concept ?? null)}
                  onBlur={field.handleBlur}
                  options={SEVERITY_OPTIONS.map((o) => ({ value: o.key, label: o.label }))}
                />
              );
            }}
          </form.AppField>

          {patientLocked && !isEditing && (
            <form.AppField name="attachedConditionIds">
              {(field) => (
                <field.SwitchListField
                  label="Manifestation of (optional)"
                  options={attachableConditions.map((c) => conditionToggleOption(c))}
                  empty="No active diagnoses to attach to. Symptom will be unattributed."
                />
              )}
            </form.AppField>
          )}

          <form.AppField name="note">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="symptom-note">
                  Note <span className="text-muted-foreground">optional</span>
                </Label>
                <Textarea
                  id="symptom-note"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Clinical narrative or context…"
                  rows={3}
                />
              </div>
            )}
          </form.AppField>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <form.AppForm>
              <form.SubmitButton>{isEditing ? "Save" : "Record Symptom"}</form.SubmitButton>
            </form.AppForm>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
